import { BridgeDirection, type LiFiQuoteResponse, type ZeroXQuoteResponse } from "@umami/swap";
import { formatEther } from "ethers";

export interface FeeEstimate {
  amount: number;
  symbol: string;
  usdValue: number;
  nativeFee: bigint;
  zroFee: bigint;
  nativeFees?: Array<{
    amount: bigint;
    symbol: string;
    chainId?: string;
  }>;
}

export interface FeeEstimationParams {
  direction: BridgeDirection;
  provider?: string;
  zeroXQuote?: ZeroXQuoteResponse | null;
  lifiQuote?: LiFiQuoteResponse | null;
  layerZeroQuote?: any | null;
  estimateFee: (direction: BridgeDirection) => Promise<{ nativeFee: bigint; zroFee: bigint }>;
  getTokenUsdPrice: (symbol: string, amount: number) => number;
  sellToken?: any;
  buyToken?: any;
}

export class FeeEstimationService {
  static async estimateFee(params: FeeEstimationParams): Promise<FeeEstimate | null> {
    const {
      direction,
      provider,
      zeroXQuote,
      lifiQuote,
      layerZeroQuote,
      estimateFee,
      getTokenUsdPrice,
      sellToken,
      buyToken,
    } = params;

    try {
      if (zeroXQuote) {
        return this.estimateZeroXFee(zeroXQuote, getTokenUsdPrice);
      }

      if (lifiQuote) {
        return this.estimateLiFiFee(lifiQuote, sellToken, buyToken);
      }

      if (provider === "LayerZero" && layerZeroQuote) {
        return this.estimateLayerZeroFee(layerZeroQuote, getTokenUsdPrice);
      }

      return await this.estimateBridgeFee(direction, estimateFee, getTokenUsdPrice);
    } catch (error) {
      console.error("Error fetching fee estimate:", error);

      return null;
    }
  }

  private static estimateZeroXFee(
    zeroXQuote: ZeroXQuoteResponse,
    getTokenUsdPrice: (symbol: string, amount: number) => number
  ): FeeEstimate {
    const totalFee = zeroXQuote.totalNetworkFee ? BigInt(zeroXQuote.totalNetworkFee) : 0n;
    const feeInEth = formatEther(totalFee);
    const feeAmount = Number(feeInEth);
    const usdValue = getTokenUsdPrice("ETH", feeAmount);

    return {
      amount: feeAmount,
      symbol: "ETH",
      usdValue,
      nativeFee: totalFee,
      zroFee: 0n,
    };
  }

  private static estimateLiFiFee(
    lifiQuote: LiFiQuoteResponse,
    sellToken?: any,
    buyToken?: any
  ): FeeEstimate {
    const gasCostUSD = lifiQuote.estimatedGas ? Number(lifiQuote.estimatedGas) : 0;

    const nativeFeesMap = new Map<string, { amount: bigint; chainId?: string }>();

    lifiQuote.route.steps.forEach((step, stepIndex) => {
      // Process gas costs (network fees)
      if (step.estimate.gasCosts) {
        step.estimate.gasCosts.forEach(gasCost => {
          const symbol = gasCost.token.symbol || "ETH";
          const amount = BigInt(gasCost.amount || "0");

          let chainId: string | undefined;

          if (sellToken && buyToken) {
            if (stepIndex === 0) {
              chainId = sellToken.chainId;
            } else if (stepIndex === lifiQuote.route.steps.length - 1) {
              chainId = buyToken.chainId;
            }
          }

          const key = `${symbol}-${chainId || "unknown"}`;

          if (nativeFeesMap.has(key)) {
            const existing = nativeFeesMap.get(key)!;
            existing.amount += amount;
          } else {
            nativeFeesMap.set(key, { amount, chainId });
          }
        });
      }

      // Process fee costs (provider fees)
      if (step.estimate.feeCosts) {
        step.estimate.feeCosts
          .filter(feeCost => !feeCost.included)
          .forEach(feeCost => {
            const symbol = feeCost.token.symbol || "ETH";
            const amount = BigInt(feeCost.amount || "0");

            let chainId: string | undefined;

            if (sellToken && buyToken) {
              if (stepIndex === 0) {
                chainId = sellToken.chainId;
              } else if (stepIndex === lifiQuote.route.steps.length - 1) {
                chainId = buyToken.chainId;
              }
            }

            const key = `${symbol}-${chainId || "unknown"}`;

            if (nativeFeesMap.has(key)) {
              const existing = nativeFeesMap.get(key)!;
              existing.amount += amount;
            } else {
              nativeFeesMap.set(key, { amount, chainId });
            }
          });
      }
    });

    const nativeFees = Array.from(nativeFeesMap.entries()).map(([key, { amount, chainId }]) => {
      const symbol = key.split("-")[0];

      return {
        amount,
        symbol,
        chainId,
      };
    });

    return {
      amount: gasCostUSD,
      symbol: "USD",
      usdValue: gasCostUSD,
      nativeFee: 0n,
      zroFee: 0n,
      nativeFees: nativeFees.length > 0 ? nativeFees : undefined,
    };
  }

  private static estimateLayerZeroFee(
    layerZeroQuote: any,
    getTokenUsdPrice: (symbol: string, amount: number) => number
  ): FeeEstimate {
    const nativeFeeInEth = parseFloat(formatEther(layerZeroQuote.nativeFee));
    const feeSymbol = layerZeroQuote.nativeFeeSymbol;
    const usdValue = getTokenUsdPrice(feeSymbol, nativeFeeInEth);

    return {
      amount: nativeFeeInEth,
      symbol: feeSymbol,
      usdValue,
      nativeFee: layerZeroQuote.nativeFee,
      zroFee: layerZeroQuote.zroFee,
    };
  }

  private static async estimateBridgeFee(
    direction: BridgeDirection,
    estimateFee: (direction: BridgeDirection) => Promise<{ nativeFee: bigint; zroFee: bigint }>,
    getTokenUsdPrice: (symbol: string, amount: number) => number
  ): Promise<FeeEstimate> {
    const fee = await estimateFee(direction);
    const tokenSymbol = this.getTokenSymbolForDirection(direction);
    const feeInEth = formatEther(fee.nativeFee);
    const feeAmount = Number(feeInEth);
    const usdValue = getTokenUsdPrice(tokenSymbol, feeAmount);

    return {
      amount: feeAmount,
      symbol: tokenSymbol,
      usdValue,
      nativeFee: fee.nativeFee,
      zroFee: fee.zroFee,
    };
  }

  private static getTokenSymbolForDirection(direction: BridgeDirection): string {
    switch (direction) {
      case BridgeDirection.EthereumToEtherlink:
      case BridgeDirection.EthereumToBsc:
        return "ETH";
      case BridgeDirection.BscToEtherlink:
      case BridgeDirection.BscToEthereum:
        return "BSC";
      case BridgeDirection.TezosToEtherlink:
      case BridgeDirection.EtherlinkToTezos:
      case BridgeDirection.EtherlinkToEthereum:
      case BridgeDirection.EtherlinkToBsc:
      case BridgeDirection.EtherlinkToEtherlink:
      default:
        return "XTZ";
    }
  }
}
